import React, { useState, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import makeStyles from '@mui/styles/makeStyles';
import {
  TableCell,
  FormGroup,
  FormControlLabel,
  Switch,
  Typography
} from '@mui/material';
import classNames from 'classnames';
import { CollectorSettingScope, Setting } from 'graphql/types';
import { useMessage } from 'modules/intl';
import { useUpdateUserPreferenceForTenantCollectorSetting } from './hooks/useUpdateUserPreferenceForTenantCollectorSetting';

const useStyles = makeStyles((theme) => ({
  status: {
    '& > label': {
      marginRight: theme.spacing(0)
    }
  },
  statusDisabled: {
    opacity: '0.6'
  }
}));
type UserPreferencesInfo = {
  installId: string;
  name: string;
  snackBarMessage: string | undefined;
};
type onOptOut = () => void;
type StatusCellProps = {
  scope: CollectorSettingScope;
  collectorName: string;
  settings: Setting[];
  installId: string;
  optInStatus: boolean;
  setOpenConfirmDialog: (value: boolean) => void;
  setUserPreferencesInfo: (value: UserPreferencesInfo) => void;
  setOnOptOut: (value: onOptOut) => void;
  setOpenSnackBar: (value: boolean) => void;
  allowUserToOptOut: boolean;
};
export const StatusCell: React.FC<StatusCellProps> = ({
  scope,
  collectorName,
  installId,
  optInStatus,
  setOpenConfirmDialog,
  setUserPreferencesInfo,
  setOnOptOut,
  setOpenSnackBar,
  allowUserToOptOut
}: StatusCellProps) => {
  const classes = useStyles();
  const [allowUsersToOptOut, setAllowUsersToOptOut] = useState(true);
  const [checked, setChecked] = useState(optInStatus);
  const optedInMessage = useMessage(
    'installedCollectors.snackBarMessage.optedIn'
  );
  const optedOutMessage = useMessage(
    'installedCollectors.snackBarMessage.optedOut'
  );
  const {
    updateUserPreferences
  } = useUpdateUserPreferenceForTenantCollectorSetting();
  const saveUserPreferences = useCallback(
    async (checked) => {
      setChecked(checked);
      await updateUserPreferences({
        installId: installId,
        settings: [
          {
            key: 'companyCollectorOptOutEnabled',
            value: checked ? 'true' : 'false'
          }
        ]
      });
      setOpenSnackBar(true);
    },
    [installId, updateUserPreferences, setOpenSnackBar]
  );

  const toggleChecked = useCallback(
    async (event) => {
      const info = {
        installId,
        name: collectorName,
        snackBarMessage: event.target.checked ? optedInMessage : optedOutMessage
      };
      setUserPreferencesInfo(info);
      setOpenSnackBar(false);
      if (event.target.checked) {
        saveUserPreferences(event.target.checked);
      } else {
        const optOut = () => () => {
          saveUserPreferences(false);
        };
        setOnOptOut(optOut);
        setOpenConfirmDialog(true);
      }
    },
    [
      installId,
      collectorName,
      optedInMessage,
      optedOutMessage,
      setUserPreferencesInfo,
      saveUserPreferences,
      setOpenConfirmDialog,
      setOnOptOut,
      setOpenSnackBar
    ]
  );
  useMemo(() => {
    setAllowUsersToOptOut(allowUserToOptOut);
  }, [allowUserToOptOut]);

  return (
    <TableCell data-testid={'status-cell'}>
      <FormGroup
        className={classNames({
          [classes.statusDisabled]:
            !allowUsersToOptOut && scope === CollectorSettingScope.Tenant
        })}
      >
        {scope === CollectorSettingScope.Tenant ? (
          <Typography className={classes.status} data-testid={'toggle-status'}>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={checked}
                  onChange={toggleChecked}
                  data-testid={'status-switch'}
                  disabled={!allowUsersToOptOut}
                />
              }
              label=""
              labelPlacement="end"
            />
            {checked ? (
              <FormattedMessage id="installationOpted.in" />
            ) : (
              <FormattedMessage id="installationOpted.out" />
            )}
          </Typography>
        ) : (
          <Typography data-testid={'text-status'}>
            <FormattedMessage id="installationOpted.enabled" />
          </Typography>
        )}
      </FormGroup>
    </TableCell>
  );
};

export default React.memo(StatusCell);
